<script setup lang="ts">
  import {
    MoveWithUsChangeOfTenancyStatus,
    type MoveWithUsChangeOfTenancy,
  } from "@/api/model"
  import { ExternalRoute } from "@/constants/externalRoute"
  import { formatDate } from "@/lib/dateHelper"
  import { buildFullAddressLine } from "@/lib/shared/addressHelper"
  import { useGetAccountId } from "@/lib/useGetAccountId"
  import ErrorReporter from "@/services/ErrorReporter"
  import { computed, watch } from "vue"
  import { Alert, AlertAction, AlertDescription } from "@/components/ui/alert"

  import CancelMoveWithUsDialog from "@/views/myaccount/moving-home/CancelMoveWithUsDialog.vue"
  import { useMoveOutSummary } from "@/views/myaccount/moving-home/useMoveOutSummary"

  const props = defineProps<{
    record: MoveWithUsChangeOfTenancy
  }>()

  const { accountId } = useGetAccountId()

  const moveInDateFormatted = computed(() =>
    formatDate(props.record.moveInDate, "ordinal", "api"),
  )

  const newAddressFormatted = computed(() =>
    buildFullAddressLine(props.record.newSupplyAddress),
  )

  const moveOutSummary = useMoveOutSummary()

  const isSubmitted = computed(
    () =>
      props.record.status === MoveWithUsChangeOfTenancyStatus.PENDING ||
      props.record.status === MoveWithUsChangeOfTenancyStatus.SUCCESSFUL,
  )

  const knownStatuses = new Set<string>(
    Object.values(MoveWithUsChangeOfTenancyStatus),
  )

  watch(
    () => props.record.status,
    (status) => {
      if (!knownStatuses.has(status)) {
        ErrorReporter.report(
          new Error(`Unknown Move With Us status: ${status}`),
        )
      }
    },
    { immediate: true },
  )
</script>

<template>
  <Alert
    v-if="record.status === MoveWithUsChangeOfTenancyStatus.FAILED"
    variant="error"
    title="We couldn't complete your move"
    data-testid="move-with-us-status-failed"
  >
    <AlertDescription>
      We couldn't set up your supply at {{ newAddressFormatted }} for
      {{ moveInDateFormatted }}. Please get in touch so we can sort this out
      with you.
    </AlertDescription>
    <CancelMoveWithUsDialog class="mt-1" :account-id="accountId" />
    <template #action>
      <AlertAction
        label="Contact support"
        :action-url="ExternalRoute.getSupport"
      />
    </template>
  </Alert>

  <Alert
    v-else-if="isSubmitted"
    variant="success"
    role="status"
    title="You're all set - we're moving with you!"
    :data-testid="`move-with-us-status-${record.status.toLowerCase()}`"
  >
    <AlertDescription>{{ moveOutSummary }}.</AlertDescription>
    <CancelMoveWithUsDialog
      v-if="record.status === MoveWithUsChangeOfTenancyStatus.PENDING"
      class="mt-1"
      :account-id="accountId"
    />
  </Alert>
</template>
